import { useState } from "react";
import { boothGames, conCalendar, dateOf, money } from "@bd/sim";
import { Icon, Sheet } from "@bd/ui-kit";
import { me, useGame } from "../store";
import { t, tdate, tx } from "../i18n";
import { BackTitle } from "./Meta";

/** The year's conventions and fairs: book a booth to show one of your games. */
export function Conventions() {
  const game = useGame((s) => s.game)!;
  const set = useGame((s) => s.set);
  const cmd = useGame((s) => s.cmd);
  const co = me(game)!;
  const [pick, setPick] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const cal = conCalendar(game);
  const games = boothGames(co, game.week);
  // the best game on sale first, then what's coming
  const best = co.products.filter((p) => games.some((g) => g.launched && g.id === p.id)).sort((a, b) => b.score - a.score)[0]?.id ?? games[0]?.id ?? "";
  const nameOf = (id: string) => games.find((g) => g.id === id)?.name ?? "";
  const book = async (con: string) => {
    const r = await cmd({ type: "bookBooth", con, game: pick[con] ?? best });
    setError(r.ok ? null : tx(r.error));
  };
  return (
    <Sheet title={<BackTitle title={t("Conventions")} onBack={() => set({ screen: { kind: "products" } })} />} onClose={() => set({ screen: null })} wide drawer testId="conventions">
      {error && <p style={{ color: "var(--bad)", marginTop: 0 }} data-testid="booth-error">{error}</p>}
      {cal.length === 0 && <p className="muted">{t("Nothing takes bookings this year.")}</p>}
      <div className="stack" style={{ gap: 6 }} title={t("A booth wins fans and a few weeks of extra sales. A game still in the works gets hype.")}>
        {cal.map((c) => {
          const booked = (co.booths ?? []).find((b) => b.con === c.id && b.week === c.week);
          const crowd = c.size >= 0.8 ? 3 : c.size >= 0.55 ? 2 : 1;
          const crowdWord = crowd === 3 ? t("Big crowd") : crowd === 2 ? t("Good crowd") : t("Small crowd");
          return (
            <div key={c.id} className="card con-card" data-testid="con">
              <span className="con-main">
                <strong>{t(c.name)}{c.awards ? <span className="con-award" title={t("Awards")} aria-label={t("Awards")}><Icon name="trophy" size={14} /></span> : null}</strong>
                <span className="ivals">
                  <span className="ival" title={tdate(dateOf(c.week).label)}><span className="ival-ic"><Icon name="calendar" size={14} /></span>{tdate(dateOf(c.week).label)}</span>
                  <span className="ival con-crowd" title={crowdWord} aria-label={crowdWord}>{Array.from({ length: 3 }, (_, i) => <Icon key={i} name="fans" size={13} style={{ opacity: i < crowd ? 1 : 0.25 }} />)}</span>
                </span>
              </span>
              {booked ? (
                <span className="tag good con-booked" data-testid="booth-booked" title={t("Booked: {game}", { game: booked.name })}><Icon name="check" size={12} /> {booked.name}</span>
              ) : (
                <span className="con-book">
                  <select value={pick[c.id] ?? best} onChange={(e) => setPick({ ...pick, [c.id]: e.target.value })} disabled={!games.length} aria-label={t("Game to show")} data-testid="booth-game">
                    {games.map((g) => <option key={g.id} value={g.id}>{g.launched ? g.name : t("{name} (coming)", { name: g.name })}</option>)}
                  </select>
                  <button className="btn small primary" disabled={!games.length || co.cash < c.cost || !nameOf(pick[c.id] ?? best)} onClick={() => void book(c.id)} title={t("Booth {m}", { m: money(c.cost) })} data-testid="book-booth">{t("Book")} · {money(c.cost)}</button>
                </span>
              )}
            </div>
          );
        })}
      </div>
    </Sheet>
  );
}
